import React, { useState, useRef } from 'react';
import { GlassPage } from '../components/chrome';
import { postJSON } from '../lib/api';

const Careers = () => {
  const [formData, setFormData] = useState({
    job: '',
    name: '',
    email: '',
    message: ''
  });
  const formRef = useRef(null);

  const handleApplyClick = (jobTitle) => {
    setFormData(prev => ({ ...prev, job: jobTitle }));
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const jobCategories = [
    {
      category: "Software & AI Engineering",
      jobs: [
        { title: "Machine Learning Engineer", desc: "Model development and optimization" },
        { title: "Data Engineer", desc: "Data pipeline and processing systems" }
      ]
    },
    {
      category: "Hardware Engineering",
      jobs: [
        { title: "Optical Engineer", desc: "Lens systems and projection technology" },
        { title: "Electrical Engineer", desc: "Circuit design and power management" },
        { title: "Hardware Engineer", desc: "Embedded systems and hardware validation" },
        { title: "Audio Engineer", desc: "Sound processing and optimization" },
        { title: "Industrial Designer", desc: "Product design and ergonomics" }
      ]
    },
    {
      category: "Manufacturing & Operations",
      jobs: [
        { title: "Manufacturing Engineer", desc: "Production processes and assembly" },
        { title: "Supply Chain Specialist", desc: "Procurement and logistics coordination" },
        { title: "Operations Coordinator", desc: "Process optimization and workflow" }
      ]
    }
  ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.message.length < 100) {
      alert('Message must be at least 100 characters');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await postJSON('/careers', {
        job: formData.job,
        name: formData.name,
        email: formData.email,
        message: formData.message || null,
      });
      setSubmitStatus('success');
      setFormData({ job: '', name: '', email: '', message: '' });
    } catch (err) {
      console.error(err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const inputClass = 'glass-input w-full px-5 py-3.5';
  const labelClass = 'block text-[10px] font-bold uppercase tracking-widest mb-2 text-zinc-500';

  return (
    <GlassPage>
      {/* Hero Section */}
      <section className="relative flex flex-col justify-center px-6 md:px-12 pt-40 pb-12">
        <div className="max-w-[1600px] mx-auto w-full">
          <h1 className="text-[12vw] md:text-[6vw] font-bold leading-[0.9] tracking-tighter mb-10 uppercase text-zinc-900">
            Join Our<br/>Team
          </h1>
          <p className="text-lg md:text-xl text-zinc-600 font-medium max-w-2xl">
            Explore open positions by category and help us build the future of assistive technology.
          </p>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-12 md:py-16 px-4 md:px-6">
        <div className="max-w-[1600px] mx-auto">
          {jobCategories.map((category, idx) => (
            <div key={idx} className="mb-16 last:mb-0">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tighter uppercase mb-8 text-zinc-900 px-2">
                {category.category}
              </h2>
              <div className="space-y-4">
                {category.jobs.map((job, jobIdx) => (
                  <div
                    key={jobIdx}
                    className="group glass p-6 hover:bg-white/70 transition-all duration-300"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold tracking-tight mb-2 text-zinc-900">{job.title}</h3>
                        <p className="text-sm text-zinc-600">{job.desc}</p>
                      </div>
                      <button
                        onClick={() => handleApplyClick(job.title)}
                        className="btn-dark px-6 py-3 self-start md:self-center whitespace-nowrap"
                      >
                        Apply Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section ref={formRef} className="py-12 md:py-16 px-4 md:px-6">
        <div className="glass-strong max-w-3xl mx-auto px-8 md:px-12 py-12 md:py-16">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tighter uppercase mb-4 text-zinc-900">
            Contact Us About a Job
          </h2>
          <p className="text-zinc-600 mb-12">
            Interested in a position? Fill out the form below and we'll get back to you.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className={labelClass}>Job Position</label>
              <select
                name="job"
                value={formData.job}
                onChange={handleChange}
                required
                className={`${inputClass} appearance-none cursor-pointer`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2318181b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1.25rem center'
                }}
              >
                <option value="">Select a position</option>
                {jobCategories.flatMap(cat =>
                  cat.jobs.map(job => (
                    <option key={job.title} value={job.title}>{job.title}</option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className={labelClass}>Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="Your name"
              />
            </div>

            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className={labelClass}>Message (min. 100 characters)</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                minLength={100}
                rows={6}
                className={`${inputClass} resize-none`}
                placeholder="Tell us why you're interested in this position..."
              />
              <p className="text-xs text-zinc-500 mt-2">
                {formData.message.length} / 100 characters
              </p>
            </div>

            <button type="submit" disabled={isSubmitting} className={`btn-dark w-full md:w-auto px-12 ${isSubmitting ? 'opacity-60 cursor-wait' : ''}`}>
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>

            {submitStatus === 'success' && (
              <p className="text-[10px] uppercase tracking-widest text-green-600 font-bold">
                Application sent. We'll be in touch.
              </p>
            )}
            {submitStatus === 'error' && (
              <p className="text-[10px] uppercase tracking-widest text-red-500 font-bold">
                Something went wrong. Please try again.
              </p>
            )}
          </form>
        </div>
      </section>
    </GlassPage>
  );
};

export default Careers;
